import { useEffect, useState } from 'react';
import { EsclationStatusResponse, Status, Ticket } from '../../types/services.type';
import { Navigate } from 'react-router-dom';
import { getProductImageByName } from '../../helpers/imageHelpers';
import FilterTab from './components/FilterTab';
import { getFormattedDate } from '../../helpers/serviceHelpers';
import { useSearchParams } from 'react-router-dom';
import ActivityIndicator from '../../components/activityIndicator';
import { Box, Card, Stack, Chip, Typography } from '@mui/material';
import TrackStatusService from '../../services/getStatus.service';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import FeedbackIcon from '@mui/icons-material/Feedback';
import { CircleSharp } from '@mui/icons-material';
import HeadingWithDescription from '../../components/headingWithDescription';

const TicketTile = ({
  ticket,
  selectedTab,
  token,
}: {
  ticket: Ticket;
  selectedTab: number;
  token: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [esclationStatus, setEsclationStatus] = useState<EsclationStatusResponse | null>(null);

  const handleSelect = (ticket: Ticket) => {
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set('id', ticket.ticketNo);
    setSearchParams(newParams);
  };

  const getEsclationStatus = async () => {
    try {
      let res = await TrackStatusService.getEscalationStatusAsync(ticket?.ticketNo, token);
      setEsclationStatus(res);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getEsclationStatus();
    // eslint-disable-next-line
  }, []);

  console.log(esclationStatus)

  return (
    <Card
      key={ticket.ticketNo}
      onClick={handleSelect.bind({}, ticket)}
      className="bg-white p-5 flex flex-row gap-3 items-center cursor-pointer overflow-hidden"
    >
      <img
        alt="product"
        src={ticket?.prd_image_url ?? getProductImageByName(ticket?.product)}
        className="h-full w-16 bg-gray-50 object-contain"
      />
      <Box className="flex-1 flex flex-col gap-2">
        <Box mt={0}>
          <Stack direction={'row'} alignItems={'flex-start'} gap={0.5}>
            <Typography lineHeight={1} flex={1} variant="button">
              {ticket?.model}
            </Typography>
            {selectedTab === 0 &&
              (esclationStatus?.status === 'true' ? (
                <Stack mt={-0.7} gap={1} alignSelf={'flex-start'} direction={'row'}>
                  <Box>
                    <CircleSharp
                      className="relative top-0 left-0"
                      sx={{ fontSize: '10px' }}
                      color="success"
                    />
                    <CircleSharp
                      className="relative top-0 left-0 animate-ping"
                      sx={{ fontSize: '14px', ml: '-12px' }}
                      color="success"
                    />
                  </Box>
                  <Chip
                    className="self-end"
                    color="primary"
                    size="small"
                    variant="outlined"
                    label={'Escalated'}
                  />
                </Stack>
              ) : (
                ticket.status !== Status.Resolved && (
                  <Chip
                    className="self-start"
                    color="primary"
                    size="small"
                    variant="outlined"
                    label={'View / Escalate'}
                  />
                )
              ))}
          </Stack>
          <Typography display={'block'} variant="caption">
            {ticket?.callType} • {getFormattedDate(ticket?.callBookDate)}
          </Typography>
          <Typography display={'block'} variant="caption" color="warning">
            {ticket?.ticketNo}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};

export default function TrackStatus() {
  const {
    userId: user_rcn,
    isAuthenticated,
    token,
  } = useSelector((state: RootState) => state.auth);
  const [activeTickets, setActiveTickets] = useState<Ticket[]>([]);
  const [closedTickets, setClosedTickets] = useState<Ticket[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchTickets = async () => {
    setLoading(true);
    if (!user_rcn || !token) return;
    try {
      const { getAllActiveStatusAsync, getAllClosedStatusAsync } = TrackStatusService;
      const activeRes = getAllActiveStatusAsync(user_rcn, token);
      const closeRes = getAllClosedStatusAsync(user_rcn, token);

      const [_active, _closed] = await Promise.all([activeRes, closeRes]);

      setActiveTickets(_active);
      setClosedTickets(_closed);
    } catch (error) {
      console.log('Error in fetchTickets: ', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    window.scrollTo({ top: 0, left: 0 });
    // eslint-disable-next-line
  }, []);

  if (!isAuthenticated) return <Navigate to="/login" />;

  const tickets = selectedTab === 0 ? activeTickets : closedTickets;

  return (
    <>
      <Stack p={2}>
        <HeadingWithDescription heading="Track Status" />
      </Stack>
      <FilterTab value={selectedTab} handleChange={setSelectedTab} />
      <Stack gap={1} p={2} bgcolor={'#f7f7f7'} flex={1}>
        {loading && <ActivityIndicator />}
        {tickets.map((ticket: Ticket) => {
          return (
            <TicketTile
              key={ticket?.ticketNo}
              selectedTab={selectedTab}
              ticket={ticket}
              token={token ?? ''}
            />
          );
        })}
        {tickets?.length === 0 && !loading && (
          <Stack alignItems={'center'} justifyContent={'center'} gap={1}>
            <FeedbackIcon color="secondary" sx={{ fontSize: 50 }} />
            <Typography color="secondary">No tickets to show</Typography>
          </Stack>
        )}
      </Stack>
    </>
  );
}
